import { useEffect, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { KPIsWidgetEnum, LongTitles } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { useKPIGetAssignmentsPlannedByAdmin } from 'api/kpi/kpi';
import {
  KPIAssignmentsPlanningDataPoint,
  KPIAssignmentsPlanningResult,
} from 'api/model';
import useTranslations from 'contexts/basicData/useTranslations';

type SortField = 'adminName' | 'assignmentsPlanned';
type SortDirection = 'asc' | 'desc';

const SortIndicator = ({
  field,
  sortField,
  sortDirection,
}: {
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
}) => {
  if (sortField !== field) return null;
  return <span>{sortDirection === 'asc' ? ' \u25B2' : ' \u25BC'}</span>;
};

const AssignmentsPlanningWidget = () => {
  const [sortField, setSortField] = useState<SortField>('assignmentsPlanned');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedAdmins, setExpandedAdmins] = useState<Set<number>>(new Set());

  const { caseTypes } = useTranslations();

  const {
    dashboardState: { daterange },
  } = useDashboardControls();

  const getAssignmentsPlannedData = useKPIGetAssignmentsPlannedByAdmin();

  const startDate = daterange[0];
  const endDate = daterange[1];
  const startDateTime = startDate ? startDate.getTime() : null;
  const endDateTime = endDate ? endDate.getTime() : null;

  useEffect(() => {
    if (startDate && endDate) {
      getAssignmentsPlannedData.mutate({
        data: {
          startDate,
          endDate,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateTime, endDateTime]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const toggleExpanded = (adminID: number) => {
    setExpandedAdmins((prev) => {
      const next = new Set(prev);
      if (next.has(adminID)) {
        next.delete(adminID);
      } else {
        next.add(adminID);
      }
      return next;
    });
  };

  const sortData = (
    data: KPIAssignmentsPlanningDataPoint[]
  ): KPIAssignmentsPlanningDataPoint[] => {
    return [...data].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'adminName':
          aVal = a.adminName || '';
          bVal = b.adminName || '';
          break;
        case 'assignmentsPlanned':
          aVal = a.assignmentsPlanned;
          bVal = b.assignmentsPlanned;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal as string);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return sortDirection === 'asc'
        ? aVal - (bVal as number)
        : (bVal as number) - aVal;
    });
  };

  const currentData =
    (getAssignmentsPlannedData.data as KPIAssignmentsPlanningResult | undefined)
      ?.dataPoints || [];
  const sortedData = sortData(currentData);
  const totalPlanned = currentData.reduce(
    (sum: number, dp: KPIAssignmentsPlanningDataPoint) =>
      sum + dp.assignmentsPlanned,
    0
  );

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.AssignmentsPlanning]}
      count={totalPlanned}
      isLoading={getAssignmentsPlannedData.isLoading}
      info="Visar hur m&aring;nga uppdrag varje admin-anv&auml;ndare har planerat (satt estimerad uppdragstid) f&ouml;r uppdrag som &auml;r bokade inom vald period. Perioden avser uppdragets bokade datum, inte n&auml;r estimatet sattes. Klicka p&aring; en rad f&ouml;r att se uppdelning per &auml;rendetyp och l&auml;n."
    >
      {sortedData.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {getAssignmentsPlannedData.isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <p>Ingen data att visa f&ouml;r vald filtrering.</p>
          )}
        </div>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead
              style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}
            >
              <tr>
                <th
                  onClick={() => handleSort('adminName')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'left',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Admin
                  <SortIndicator
                    field="adminName"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('assignmentsPlanned')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Antal planerade
                  <SortIndicator
                    field="assignmentsPlanned"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((dp) => {
                const isExpanded = expandedAdmins.has(dp.adminID);
                const hasDetails = dp.details && dp.details.length > 0;
                return (
                  <>
                    <tr
                      key={dp.adminID}
                      onClick={() => hasDetails && toggleExpanded(dp.adminID)}
                      style={{
                        borderBottom: '1px solid #eee',
                        cursor: hasDetails ? 'pointer' : 'default',
                        backgroundColor: isExpanded ? '#f8f9fa' : undefined,
                      }}
                    >
                      <td style={{ padding: '8px' }}>
                        {hasDetails && (
                          <FontAwesomeIcon
                            icon={isExpanded ? faChevronDown : faChevronRight}
                            style={{
                              marginRight: '8px',
                              fontSize: '0.75em',
                              color: '#666',
                            }}
                          />
                        )}
                        {dp.adminName || '(Ok\u00e4nd)'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        {dp.assignmentsPlanned}
                      </td>
                    </tr>
                    {isExpanded &&
                      dp.details.map((detail, idx) => (
                        <tr
                          key={`${dp.adminID}-${idx}`}
                          style={{
                            borderBottom: '1px solid #f0f0f0',
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <td
                            style={{
                              padding: '4px 8px 4px 32px',
                              fontSize: '0.9em',
                              color: '#555',
                            }}
                          >
                            {caseTypes[detail.caseType] || '(Ingen typ)'} &mdash;{' '}
                            {detail.countyName || '(Inget l\u00e4n)'}
                          </td>
                          <td
                            style={{
                              padding: '4px 8px',
                              textAlign: 'right',
                              fontSize: '0.9em',
                              color: '#555',
                            }}
                          >
                            {detail.count}
                          </td>
                        </tr>
                      ))}
                  </>
                );
              })}
              <tr
                style={{
                  borderTop: '2px solid #ddd',
                  fontWeight: 'bold',
                }}
              >
                <td style={{ padding: '8px' }}>Totalt</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>
                  {totalPlanned}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Widget>
  );
};

export default AssignmentsPlanningWidget;
